import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import SingleSelect from "components/common/SingleSelect";
import Switch from "components/common/Switch";
import ConditionRuleInput, {
  getEventDefault,
  getEventOptions,
  getEventType,
} from "components/form/ConditionRuleInput";
import CustomError from "components/form/CustomError";
import CustomField from "components/form/CustomField";
import TimePicker from "components/form/TimePicker";
import { FieldArray, Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPlansList } from "store/slices/membershipSlice";
import {
  addNotificationRule,
  getNotificationRuleDetails,
  updateNotificationRule,
} from "store/slices/notificationRulesSlice";
import {
  AUDIENCE_TYPES,
  LOGICAL_OPERATORS,
  SEGMENTS_BY_CATEGORY,
} from "utils/constants";
import {
  dateToTimeString,
  timeStringToDate,
} from "utils/utils";
import { getNotificationRuleValidationSchema } from "utils/validations";

const AddEditRule = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isEdit = mode === "edit";
  const id = useQueryParam("id");
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("notifications");

  const { itemDetails, loading, isLoading } = useSelector(
    (state) => state.NotificationRules,
  );

  const plansList = useSelector((state) => state.Membership.plansList);

  useEffect(() => {
    dispatch(getPlansList());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && id && canEdit) {
      dispatch(getNotificationRuleDetails(id));
    }
  }, [isEdit, id, dispatch, canEdit]);

  const [isScheduleTime, setIsScheduleTime] = useState(false);

  const [initialValues, setInitialValues] = useState({
    name: "",
    category: "WASH",
    audience: {
      type: "ALL",
      logicalOperator: "AND",
      conditions: [],
      segments: [{ type: "", config: {} }],
    },
    content: {
      title: { en: "", ar: "" },
      body: { en: "", ar: "" },
    },
    notificationTime: "",
  });

  useEffect(() => {
    if (isEdit && itemDetails) {
      const { name, category, audience, content, execution } = itemDetails;

      const timeValue = execution?.executionTime?.time || "";
      if (timeValue !== "") {
        setIsScheduleTime(true);
      } else {
        setIsScheduleTime(false);
      }

      setInitialValues({
        name,
        category: category || "WASH",
        audience: {
          type: audience?.type || "ALL",
          logicalOperator: audience?.logicalOperator || "AND",
          conditions: audience?.conditions || [],
          segments: audience?.segments || [{ type: "", config: {} }],
        },
        content: {
          title: {
            en: content?.title?.en || "",
            ar: content?.title?.ar || "",
          },
          body: {
            en: content?.body?.en || "",
            ar: content?.body?.ar || "",
          },
        },
        notificationTime: timeValue,
      });
    }
  }, [isEdit, itemDetails]);

  const handleSubmit = (values) => {
    const { notificationTime, audience, category, ...rest } = values || {};

    const formattedAudience = { type: audience.type };

    if (audience.type === "SEGMENT") {
      const validSegments = audience.segments
        .filter((seg) => seg.type)
        .map((seg) => ({
          type: seg.type,
          config: Object.keys(seg.config || {}).reduce((acc, key) => {
            const value = seg.config[key];
            acc[key] =
              typeof value === "string" && !isNaN(value)
                ? Number(value)
                : value;
            return acc;
          }, {}),
        }));

      if (validSegments.length > 0) {
        formattedAudience.segments = validSegments;
      }
    } else if (audience.type === "CUSTOM") {
      const validConditions = audience.conditions
        .filter((cond) => cond.event)
        .map((cond) => ({
          event: cond.event,
          operator: cond.operator,
          value:
            cond.type === "number" && typeof cond.value === "string"
              ? Number(cond.value)
              : cond.value,
          type: cond.type === "plan" ? "string" : cond.type,
        }));

      if (validConditions.length > 0) {
        formattedAudience.conditions = validConditions;
        formattedAudience.logicalOperator = audience.logicalOperator || "AND";
      }
    }

    const payload = {
      name: rest.name,
      category: category,
      audience: formattedAudience,
      content: rest.content,
      execution: {
        executionTime: { time: notificationTime },
      },
    };

    if (isEdit) {
      handleSubmitForm({
        dispatch,
        action: updateNotificationRule,
        payload: { id, ...payload },
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addNotificationRule,
        payload: payload,
        onSuccess: () => navigate(-1),
      });
    }
  };

  const getSegmentDescription = (
    segmentType,
    segmentIndex,
    values,
    setFieldValue,
  ) => {
    const descriptions = {
      SINGLE_VEHICLE: {
        text: "Vehicle count = ",
        fields: [
          { key: "count", type: "number", min: 1, max: 1, readOnly: true },
        ],
      },
      MULTIPLE_VEHICLE: {
        text: "Vehicle count > ",
        fields: [{ key: "count", type: "number", min: 1 }],
      },
      ONE_TIME_CUSTOMERS: {
        text: "Exactly 1 wash completed in the last ",
        fields: [{ key: "days", type: "number", min: 1, suffix: " days" }],
      },
      REPEAT_CUSTOMERS: {
        text: "More than 1 wash completed in the last ",
        fields: [{ key: "days", type: "number", min: 1, suffix: " days" }],
      },
      NO_ACTIVE_PLAN_USERS: { text: "No active wash plan", fields: [] },
      INACTIVE_CUSTOMERS: {
        text: "No wash in the last ",
        fields: [{ key: "days", type: "number", min: 1, suffix: " days" }],
      },
      NO_WASH_SINCE: {
        text: "No wash since last",
        fields: [{ key: "days", type: "number", min: 1, suffix: " days" }],
      },
      DORMANT_CUSTOMERS: {
        text: "No wash between ",
        fields: [
          { key: "minDays", type: "number", min: 1, suffix: " to " },
          { key: "maxDays", type: "number", min: 1, suffix: " days" },
        ],
      },
      EXPIRING_SOON: {
        text: "Membership expires within ",
        fields: [{ key: "days", type: "number", min: 1, suffix: " days" }],
      },
      EXPIRED_MEMBERS: { text: "Membership status = Expired", fields: [] },
      BIRTHDAY: { text: "Happy Birthday", fields: [] },
      MEMBERSHIP_PURCHASED: { text: "Purchased Membership", fields: [] },
      ACCOUNT_CREATED: { text: "New Account Created", fields: [] },
      ACTIVE_MEMBERS: { text: "Membership status = Active", fields: [] },
      MEMBERSHIP_ON_HOLD: { text: "Membership status = Paused", fields: [] },
      ONLY_EXTERIOR_MEMBERS: { text: "No interior wash ever", fields: [] },
      NEW_CUSTOMERS: { text: "No wash completed till date", fields: [] },
      ACTIVE_CUSTOMERS: {
        text: "At least 1 wash in the last ",
        fields: [{ key: "days", type: "number", min: 1, suffix: " days" }],
      },
      ADD_ON_PROMOTION: {
        text: "User at least",
        fields: [
          {
            key: "count",
            type: "number",
            min: 1,
            suffix: " times washes without interior",
          },
        ],
      },
      STORE_NEARBY_USERS: {
        text: "User location within ",
        fields: [
          {
            key: "radiusInKm",
            type: "number",
            min: 1,
            suffix: " km radius of store",
          },
        ],
      },
    };

    const description = descriptions[segmentType];
    if (!description) return null;

    return (
      <div
        className="segment-description mt-2 p-3"
        style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}
      >
        <p className="mb-0 d-flex align-items-center flex-wrap gap-2">
          <span style={{ fontWeight: 500 }}>
            {description.text}
            {description.fields.map((field) => (
              <React.Fragment key={field.key}>
                <input
                  type={field.type}
                  className="form-control d-inline-block dynamic_form_field"
                  value={
                    values?.audience?.segments?.[segmentIndex]?.config?.[
                      field.key
                    ] || ""
                  }
                  onChange={(e) => {
                    const value =
                      field.type === "number"
                        ? Number(e.target.value)
                        : e.target.value;
                    setFieldValue(
                      `audience.segments.${segmentIndex}.config.${field.key}`,
                      value,
                    );
                  }}
                  min={field.min}
                  max={field.max}
                  readOnly={field.readOnly}
                  required
                />
                {field.suffix && <span>{field.suffix}</span>}
              </React.Fragment>
            ))}
          </span>
        </p>
      </div>
    );
  };

  const CATEGORY_OPTIONS = [
    { name: "User", value: "USER" },
    { name: "Wash", value: "WASH" },
    { name: "Membership", value: "MEMBERSHIP" },
    { name: "Engagement", value: "ENGAGEMENT" },
    { name: "Store", value: "STORE" },
  ];

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading)
    return (
      <AddEditLoader title={t("Edit Notification Rule")} rows={6} cols={3} />
    );

  return (
    <div className="page-content add_user_page">
      <PageHeader
        isBackBtn={true}
        title={
          isEdit ? t("Edit Notification Rule") : t("Add Notification Rule")
        }
      />
      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={getNotificationRuleValidationSchema(
            t,
            isScheduleTime,
          )}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <h6 className="mb-3 bold">{t("Compose Broadcast")}</h6>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Rule Name")}
                      name="name"
                      placeholder={t("e.g., Re-engagement Campaign - 30 Days")}
                      required
                    />
                    <p className="small sub_text mt-1">
                      {t("Internal name for your reference.")}
                    </p>
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <SingleSelect
                      required
                      label={t("Category")}
                      name="category"
                      options={CATEGORY_OPTIONS}
                      value={values?.category}
                      handleSingleSelectChange={(opt) => {
                        setFieldValue("category", opt.value);
                        setFieldValue(`audience.segments.0.type`, "");
                        if (values?.audience?.type === "CUSTOM") {
                          setFieldValue(
                            "audience.conditions",
                            values?.audience?.conditions.map((c) => ({
                              ...c,
                              event: "",
                            })),
                          );
                        }
                      }}
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <SingleSelect
                      required
                      label={t("Audience Type")}
                      name="audience.type"
                      options={AUDIENCE_TYPES}
                      value={values?.audience?.type}
                      handleSingleSelectChange={(opt) => {
                        setFieldValue("audience.type", opt.value);
                        if (opt.value === "ALL") {
                          setFieldValue("audience.segments", [
                            { type: "", config: {} },
                          ]);
                          setFieldValue("audience.conditions", []);
                        } else if (opt.value === "SEGMENT") {
                          setFieldValue("audience.segments", [
                            { type: "", config: {} },
                          ]);
                          setFieldValue("audience.conditions", []);
                        } else if (opt.value === "CUSTOM") {
                          setFieldValue("audience.conditions", [
                            { event: "", operator: "", value: "" },
                          ]);
                          setFieldValue("audience.segments", [
                            { type: "", config: {} },
                          ]);
                          setFieldValue("audience.logicalOperator", "AND");
                        }
                      }}
                    />
                  </div>
                </Col>

                {/* ── SEGMENT ── */}
                {values?.audience?.type === "SEGMENT" && (
                  <Col lg={6}>
                    <div className="form_field">
                      <SingleSelect
                        required
                        label={t("Segment")}
                        name={`audience.segments.0.type`}
                        options={SEGMENTS_BY_CATEGORY[values?.category] || []}
                        value={values?.audience?.segments?.[0]?.type}
                        handleSingleSelectChange={(opt) => {
                          const selectedValue =
                            opt?.value || opt?.target?.value || opt;
                          const cleanValue =
                            typeof selectedValue === "string"
                              ? selectedValue.replace(/^["']|["']$/g, "")
                              : selectedValue;

                          setFieldValue(`audience.segments.0.type`, cleanValue);

                          const selectedSegment = SEGMENTS_BY_CATEGORY[
                            values?.category
                          ]?.find((seg) => seg.value === cleanValue);
                          setFieldValue(
                            `audience.segments.0.config`,
                            selectedSegment?.config || {},
                          );
                        }}
                      />
                      <CustomError name={`audience.segments.0.type`} />
                      {values?.audience?.segments?.[0]?.type &&
                        getSegmentDescription(
                          values.audience.segments[0].type,
                          0,
                          values,
                          setFieldValue,
                        )}
                    </div>
                  </Col>
                )}

                {/* ── CUSTOM ── */}
                {values?.audience?.type === "CUSTOM" && (
                  <>
                    <Col lg={6}>
                      <div className="form_field">
                        <SingleSelect
                          required
                          label={t("Logical Operator")}
                          name={`audience.logicalOperator`}
                          options={LOGICAL_OPERATORS}
                          value={values?.audience?.logicalOperator}
                          handleSingleSelectChange={(opt) =>
                            setFieldValue(`audience.logicalOperator`, opt.value)
                          }
                        />
                        <CustomError name={`audience.logicalOperator`} />
                      </div>
                    </Col>

                    <Col lg={12}>
                      <FieldArray name="audience.conditions">
                        {({ push, remove }) => (
                          <Row>
                            {values?.audience?.conditions?.map(
                              (item, index) => {
                                const selectedEvents =
                                  values?.audience?.conditions
                                    .map((cond, idx) =>
                                      idx !== index ? cond.event : null,
                                    )
                                    .filter(Boolean);

                                return (
                                  <Row
                                    key={index}
                                    className="condition_row mb-2"
                                  >
                                    <Col lg={6}>
                                      <div className="form_field">
                                        <SingleSelect
                                          required
                                          label={t("Event")}
                                          name={`audience.conditions.${index}.event`}
                                          options={getEventOptions(
                                            selectedEvents,
                                          )}
                                          value={item.event}
                                          handleSingleSelectChange={(opt) => {
                                            const { operator, value } =
                                              getEventDefault(opt.value);
                                            const eventType = getEventType(
                                              opt.value,
                                            );
                                            setFieldValue(
                                              `audience.conditions.${index}.event`,
                                              opt.value,
                                            );
                                            setFieldValue(
                                              `audience.conditions.${index}.operator`,
                                              operator,
                                            );
                                            setFieldValue(
                                              `audience.conditions.${index}.value`,
                                              value,
                                            );
                                            setFieldValue(
                                              `audience.conditions.${index}.type`,
                                              eventType,
                                            );
                                          }}
                                        />
                                        <CustomError
                                          name={`audience.conditions.${index}.event`}
                                        />
                                      </div>
                                    </Col>

                                    <Col lg={6}>
                                      <div className="d-flex gap-3 w-100">
                                        <div className="form_field flex-grow-1">
                                          <ConditionRuleInput
                                            label={t("Trigger Condition")}
                                            eventKey={item.event}
                                            operator={item.operator}
                                            value={item.value}
                                            type={item.type}
                                            onOperatorChange={(val) =>
                                              setFieldValue(
                                                `audience.conditions.${index}.operator`,
                                                val,
                                              )
                                            }
                                            onChange={(val) =>
                                              setFieldValue(
                                                `audience.conditions.${index}.value`,
                                                val,
                                              )
                                            }
                                            plansList={plansList}
                                            min={0}
                                          />
                                        </div>
                                        {values?.audience?.conditions?.length >
                                          1 && (
                                          <X
                                            style={{
                                              marginTop: 45,
                                              minWidth: 24,
                                            }}
                                            className="text_danger c_p"
                                            onClick={() => remove(index)}
                                          />
                                        )}
                                      </div>
                                      <CustomError
                                        name={`audience.conditions.${index}.operator`}
                                      />
                                      <CustomError
                                        name={`audience.conditions.${index}.value`}
                                      />
                                    </Col>
                                  </Row>
                                );
                              },
                            )}

                            <div className="g_end">
                              <button
                                type="button"
                                className="main_btn outline"
                                onClick={() =>
                                  push({ event: "", operator: "", value: "" })
                                }
                              >
                                + {t("Add Condition")}
                              </button>
                            </div>
                          </Row>
                        )}
                      </FieldArray>
                    </Col>
                  </>
                )}

                <div className="hr"></div>
                <h6 className="mb-3 bold">{t("Message Content")}</h6>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (English)")}
                      name="content.title.en"
                      placeholder={t("Enter Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="content.title.ar"
                      required
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (Arabic)")}
                      name="content.title.ar"
                      placeholder={t("Enter Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="content.title.en"
                      required
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (English)")}
                      name="content.body.en"
                      placeholder={t("Enter Description (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="content.body.ar"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (Arabic)")}
                      name="content.body.ar"
                      placeholder={t("Enter Description (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="content.body.en"
                    />
                  </div>
                </Col>

                <div className="hr"></div>
                <h6 className="mb-3 bold">{t("Notification Time")}</h6>

                <Col lg={6}>
                  <div className="d-flex gap-5">
                    <div className="form_field">
                      <label>{t("Set schedule time")}</label>
                      <Switch
                        checked={isScheduleTime}
                        handleOnChange={(e) => {
                          setIsScheduleTime(e.target.value);
                          setFieldValue(`notificationTime`, "");
                        }}
                      />
                    </div>
                    {isScheduleTime && (
                      <div className="form_field">
                        <label>{t("Notification Time")}</label>
                        <TimePicker
                          value={
                            values?.notificationTime
                              ? timeStringToDate(values.notificationTime)
                              : null
                          }
                          onChange={(val) =>
                            setFieldValue(
                              `notificationTime`,
                              dateToTimeString(val),
                            )
                          }
                          error={
                            touched?.notificationTime &&
                            errors?.notificationTime
                          }
                        />
                      </div>
                    )}
                  </div>
                </Col>

                <Col lg={12} className="mt-3">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="main_btn outline"
                      onClick={goBack}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="main_btn"
                      disabled={isLoading}
                    >
                      {isEdit ? t("Save Changes") : t("Add Rule")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditRule;
